import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    MapPin, Building2, Wallet, Package, Ruler, Sparkles,
    ArrowRight, Check, X, Cpu, Search, Download, MessageCircle, FileText,
} from "lucide-react";
import { api, formatErr } from "../lib/api";

const PROPERTY_TYPES = ["Apartment", "Villa", "Plot", "Commercial", "Farm Land"];
const BHK_OPTIONS = ["1 BHK", "2 BHK", "3 BHK", "4+ BHK"];
const LEAD_TIERS = ["Standard", "OTP-Verified", "Ready to Visit"];

const fmtINR = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;

const CONFIDENCE_STYLE = {
    High: "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10",
    Medium: "text-white/80 border-white/25 bg-white/5",
    Low: "text-white/50 border-white/10 bg-white/[0.03]",
};

const COMPARISON = [
    { label: "AI-based market pricing", us: true, them: false },
    { label: "Micro-market tier pricing", us: true, them: false },
    { label: "Confidence-scored estimates", us: true, them: false },
    { label: "OTP-Verified Leads", us: true, them: false },
    { label: "Fresh, exclusive leads", us: true, them: false },
    { label: "Verified past campaign data", us: true, them: false },
    { label: "AI optimization", us: true, them: false },
    { label: "Volume-based pricing", us: true, them: false },
    { label: "Dedicated account manager", us: true, them: false },
    { label: "Same price for every city", us: false, them: true },
    { label: "Generic shared database", us: false, them: true },
    { label: "Old / recycled leads", us: false, them: true },
];

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "917836874807";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AICalculator() {
    const [config, setConfig] = useState(null);
    const [city, setCity] = useState(null);
    const [mm, setMm] = useState(null);
    const [propertyType, setPropertyType] = useState("Apartment");
    const [configuration, setConfiguration] = useState("2 BHK");
    const [priceBand, setPriceBand] = useState(null);
    const [leadCount, setLeadCount] = useState(200);
    const [leadTier, setLeadTier] = useState("OTP-Verified");
    const [projectName, setProjectName] = useState("");
    const [developer, setDeveloper] = useState("");
    const [positioningNote, setPositioningNote] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const [showLeadForm, setShowLeadForm] = useState(false);
    const [lead, setLead] = useState({ name: "", email: "", phone: "" });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Load config
    useEffect(() => {
        api.get("/cpl/config")
            .then(({ data }) => {
                setConfig(data);
                const cities = data.cities || [];
                if (cities.length === 0) {
                    toast.error("AI CPL engine has no active markets right now. Please contact us for a manual quote.");
                    return;
                }
                setCity(cities[0]);
                setMm(cities[0].micro_markets?.[0] || null);
                setPriceBand(data.price_bands?.[propertyType]?.[2] || data.price_bands?.[propertyType]?.[0]);
            })
            .catch((e) => toast.error(formatErr(e.response?.data?.detail) || "Could not load AI CPL engine."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset price band whenever property type changes (bands are property-type specific)
    useEffect(() => {
        if (!config) return;
        const bands = config.price_bands?.[propertyType] || [];
        setPriceBand((prev) => (bands.includes(prev) ? prev : bands[0]));
        setConfiguration(propertyType === "Apartment" || propertyType === "Villa" ? "2 BHK" : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyType, config]);

    // Recalculate on any input change (debounced, with stale-response guard)
    useEffect(() => {
        if (!city || !mm || !priceBand) return;
        let ignore = false;
        const controller = new AbortController();
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.post("/cpl/estimate", {
                    city: city.name,
                    micro_market: mm,
                    property_type: propertyType,
                    configuration: configuration || null,
                    price_band: priceBand,
                    lead_count: leadCount,
                    lead_tier: leadTier,
                    project_name: projectName || null,
                    developer: developer || null,
                    positioning_note: positioningNote || null,
                }, { signal: controller.signal });
                if (!ignore) setResult(data);
            } catch (e) {
                if (!ignore && e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
                    toast.error(formatErr(e.response?.data?.detail) || "Estimate failed");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        }, 250);
        return () => { ignore = true; controller.abort(); clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [city, mm, propertyType, configuration, priceBand, leadCount, leadTier]);

    // Close modal on Escape
    useEffect(() => {
        if (!showLeadForm) return;
        const onKey = (e) => { if (e.key === "Escape" && !saving) setShowLeadForm(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showLeadForm, saving]);

    const submitLead = async (e) => {
        e.preventDefault();
        if (!lead.name || !lead.email || !lead.phone) {
            toast.error("Please share your details so we can send your detailed estimate.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/cpl/lead", { ...lead, estimate_id: result.estimate_id });
            setSaved(true);
            toast.success("Saved. Our team will call you shortly.");
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail) || "Could not save your details.");
        } finally {
            setSaving(false);
        }
    };

    const downloadPdf = () => {
        if (!result?.estimate_id) return;
        window.open(`${API_BASE}/cpl/pdf/${result.estimate_id}`, "_blank");
    };

    const waHref = useMemo(() => {
        if (!result) return `https://wa.me/${WA_NUMBER}`;
        const msg = `Hi Lead Krafts, I got an indicative CPL of ${fmtINR(result.cpl_low)}-${fmtINR(result.cpl_high)} for ${city?.name} / ${mm} (${propertyType}). Can we talk?`;
        return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
    }, [result, city, mm, propertyType]);

    if (!config || !city) {
        return (
            <section id="calculator" className="py-24 px-6 text-center text-white/60">
                Loading AI CPL Engine…
            </section>
        );
    }

    const priceBands = config.price_bands?.[propertyType] || [];

    return (
        <section
            id="calculator"
            data-testid="ai-calculator-section"
            className="relative py-24 sm:py-32 px-6 sm:px-10 lg:px-16 overflow-hidden"
        >
            <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-[0.08] blur-[130px]"
                style={{ background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)" }} />

            <div className="relative max-w-7xl mx-auto">
                <div className="text-center max-w-3xl mx-auto mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] bg-[#D4AF37]/5 text-[10px] uppercase tracking-[0.3em] mb-6">
                        <Cpu size={12} /> AI-Estimated Indicative CPL
                    </div>
                    <h2 className="font-serif-display text-4xl sm:text-6xl leading-[1.0] tracking-tight">
                        Market-Aware <br />
                        <span className="italic text-[#D4AF37]">CPL Estimator.</span>
                    </h2>
                    <p className="mt-6 text-white/70 text-lg">
                        Estimate your indicative cost per lead based on city, micro-market, property type, ticket
                        size, lead tier and volume — under 60 seconds.
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 space-y-5">
                        <StepCard icon={MapPin} num="01" title="Location">
                            <div className="mb-4">
                                <Label>City</Label>
                                <div className="flex flex-wrap gap-2" data-testid="cities-list">
                                    {(config.cities || []).map((c) => (
                                        <Chip key={c.id} active={city.id === c.id} onClick={() => { setCity(c); setMm(c.micro_markets?.[0] || null); }} testid={`city-${c.name.replace(/\s/g, "-")}`}>
                                            {c.name}
                                        </Chip>
                                    ))}
                                </div>
                                <div className="mt-2 text-[10px] text-white/35">More Indian cities are added as market data is confirmed.</div>
                            </div>
                            <MicroMarketPicker city={city} selected={mm} onPick={setMm} />
                        </StepCard>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <StepCard icon={Building2} num="02" title="Property type">
                                <SelectGrid options={PROPERTY_TYPES} value={propertyType} onChange={setPropertyType} testid="prop-type" cols={2} />
                            </StepCard>
                            <StepCard icon={Ruler} num="03" title="Configuration">
                                <ConfigurationInput propertyType={propertyType} value={configuration} onChange={setConfiguration} />
                            </StepCard>
                        </div>

                        <StepCard icon={Wallet} num="04" title="Price / buyer budget band">
                            <SelectGrid options={priceBands} value={priceBand} onChange={setPriceBand} testid="price-band" cols={3} />
                        </StepCard>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <StepCard icon={Package} num="05" title="Lead quantity (min. 50)">
                                <div className="flex flex-wrap gap-2" data-testid="package-chips">
                                    {(config.volume_tiers || []).map((p) => (
                                        <Chip key={p} active={leadCount === p} onClick={() => setLeadCount(p)} testid={`pkg-${p}`}>
                                            {p === 2000 ? "2000+" : p}
                                        </Chip>
                                    ))}
                                </div>
                            </StepCard>
                            <StepCard icon={Sparkles} num="06" title="Lead tier">
                                <SelectGrid options={LEAD_TIERS} value={leadTier} onChange={setLeadTier} testid="lead-tier" cols={1} />
                            </StepCard>
                        </div>

                        <StepCard icon={FileText} num="07" title="Optional — for a more tailored estimate">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input data-testid="project-name-input" placeholder="Project name" className="hol-input" value={projectName} onChange={(e) => setProjectName(e.target.value)} />
                                <input data-testid="developer-input" placeholder="Developer / owner" className="hol-input" value={developer} onChange={(e) => setDeveloper(e.target.value)} />
                            </div>
                            <textarea
                                data-testid="positioning-note-input"
                                placeholder="Project positioning notes (optional)"
                                className="hol-input mt-3 min-h-[70px] resize-none"
                                value={positioningNote}
                                onChange={(e) => setPositioningNote(e.target.value)}
                            />
                            <div className="mt-2 text-[10px] text-white/35">
                                Stored for context only — never used to move the CPL number.
                            </div>
                        </StepCard>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="sticky top-32 space-y-4">
                            <ResultCard
                                result={result}
                                loading={loading}
                                onDetailedEstimate={() => setShowLeadForm(true)}
                                onDownloadPdf={downloadPdf}
                                waHref={waHref}
                            />
                        </div>
                    </div>
                </div>

                {/* Comparison table */}
                <div className="mt-24">
                    <div className="text-center mb-10">
                        <div className="overline">Lead Krafts vs Everyone Else</div>
                        <h3 className="mt-4 font-serif-display text-3xl sm:text-4xl">
                            The <span className="italic text-[#D4AF37]">difference</span> is measurable.
                        </h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4" data-testid="comparison-table">
                        {[
                            { title: "Lead Krafts", subtitle: "AI-powered · OTP-Verified", gold: true, items: COMPARISON.filter((c) => c.us) },
                            { title: "Ordinary Providers", subtitle: "Housing / MagicBricks / 99acres tier", gold: false, items: COMPARISON.filter((c) => c.them) },
                        ].map((col) => (
                            <div key={col.title} className={`p-8 ${col.gold ? "bg-[#D4AF37] text-black" : "hol-card"}`}>
                                <div className={`text-xs uppercase tracking-[0.25em] ${col.gold ? "text-black/60" : "text-white/50"}`}>
                                    {col.subtitle}
                                </div>
                                <h4 className={`mt-3 font-serif-display text-3xl ${col.gold ? "text-black" : "text-white"}`}>{col.title}</h4>
                                <ul className="mt-6 space-y-3">
                                    {col.items.map((it) => (
                                        <li key={it.label} className="flex items-start gap-3 text-sm">
                                            {col.gold ? (
                                                <Check size={16} className="text-black mt-0.5 shrink-0" strokeWidth={2.5} />
                                            ) : (
                                                <X size={16} className="text-red-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                                            )}
                                            <span className={col.gold ? "text-black/85" : "text-white/75"}>{it.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Detailed estimate modal */}
            {showLeadForm && (
                <div
                    data-testid="lead-modal"
                    className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md grid place-items-center p-6"
                    onClick={() => !saving && setShowLeadForm(false)}
                >
                    <div className="relative w-full max-w-lg bg-[#0A0B10] border border-[#D4AF37]/40 p-8" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowLeadForm(false)} className="absolute top-4 right-4 text-white/50 hover:text-[#D4AF37]"><X size={18} /></button>
                        {!saved ? (
                            <>
                                <div className="overline">Get Detailed Estimate</div>
                                <h3 className="mt-3 font-serif-display text-2xl">Get a tailored CPL breakdown by email + WhatsApp</h3>
                                <p className="mt-2 text-sm text-white/60">Indicative CPL {fmtINR(result?.cpl_low)}–{fmtINR(result?.cpl_high)} · {leadCount} leads · Confidence {result?.confidence}</p>
                                <form onSubmit={submitLead} className="mt-6 space-y-3">
                                    <input data-testid="cpl-lead-name" placeholder="Full name*" className="hol-input" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} />
                                    <input data-testid="cpl-lead-email" type="email" placeholder="Work email*" className="hol-input" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                                    <input data-testid="cpl-lead-phone" placeholder="Phone (WhatsApp)*" className="hol-input" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} />
                                    <button data-testid="cpl-lead-submit" disabled={saving} className="btn-gold w-full justify-center disabled:opacity-60">
                                        {saving ? "Saving..." : (<>Send me this estimate <ArrowRight size={16} /></>)}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-6">
                                <div className="mx-auto h-14 w-14 border border-[#D4AF37] grid place-items-center text-[#D4AF37]"><Check size={22} /></div>
                                <h3 className="mt-4 font-serif-display text-2xl">Estimate on its way.</h3>
                                <p className="mt-2 text-sm text-white/60">A senior partner will call you within 24 hours with your written proposal.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

/* ---------- Sub-components ---------- */

function StepCard({ icon: Icon, num, title, children }) {
    return (
        <div className="bg-[#0A0B10] border border-white/10 hover:border-[#D4AF37]/30 transition-colors p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 grid place-items-center border border-[#D4AF37]/40 text-[#D4AF37]">
                        <Icon size={16} strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Step {num}</div>
                        <div className="font-serif-display text-lg leading-tight">{title}</div>
                    </div>
                </div>
            </div>
            {children}
        </div>
    );
}

function Label({ children }) {
    return <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2">{children}</div>;
}

function Chip({ active, onClick, children, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`px-3.5 py-2.5 text-xs tracking-wide border transition-all ${
                active
                    ? "bg-[#D4AF37] text-black border-[#D4AF37] shadow-[0_0_16px_rgba(212,175,55,0.4)]"
                    : "border-white/10 text-white/70 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
            }`}
        >
            {children}
        </button>
    );
}

function SelectGrid({ options, value, onChange, testid, cols = 2 }) {
    const colClass = cols === 3 ? "grid-cols-2 sm:grid-cols-3" : cols === 1 ? "grid-cols-1" : "grid-cols-2";
    return (
        <div className={`grid ${colClass} gap-2`} data-testid={testid}>
            {options.map((o) => (
                <button
                    key={o}
                    type="button"
                    data-testid={`${testid}-${o.replace(/\s/g, "-").toLowerCase()}`}
                    onClick={() => onChange(o)}
                    className={`text-xs px-3 py-3 text-left border transition-all ${
                        value === o
                            ? "bg-[#D4AF37]/15 border-[#D4AF37] text-[#D4AF37]"
                            : "border-white/10 text-white/70 hover:border-[#D4AF37]/40"
                    }`}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

function ConfigurationInput({ propertyType, value, onChange }) {
    if (propertyType === "Apartment" || propertyType === "Villa") {
        return <SelectGrid options={BHK_OPTIONS} value={value} onChange={onChange} testid="config-bhk" cols={2} />;
    }
    const isFarm = propertyType === "Farm Land";
    return (
        <div>
            <input
                data-testid="config-size-input"
                type="text"
                inputMode="decimal"
                placeholder={isFarm ? "Size in acres (e.g. 1.5)" : "Size in sq. ft. (e.g. 1200)"}
                className="hol-input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
            <div className="mt-2 text-[10px] text-white/35">{isFarm ? "Decimal entry allowed." : "Enter approximate size."}</div>
        </div>
    );
}

function MicroMarketPicker({ city, selected, onPick }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => { setQ(""); setOpen(false); }, [city?.id]);

    useEffect(() => {
        const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const all = city.micro_markets || [];
    const filtered = useMemo(() => {
        if (!q.trim()) return all;
        const s = q.toLowerCase();
        return all.filter((m) => m.toLowerCase().includes(s));
    }, [q, all]);

    return (
        <div>
            <Label>Micro-market / locality in {city.name}</Label>
            <div className="relative" ref={boxRef}>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
                <input
                    data-testid="mm-search"
                    className="hol-input pl-9"
                    placeholder="Type a locality or corridor..."
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                />
                {open && (
                    <div data-testid="mm-dropdown" className="absolute z-40 mt-1 w-full max-h-72 overflow-auto bg-[#0A0B10] border border-[#D4AF37]/30 shadow-xl">
                        {filtered.length === 0 && <div className="p-4 text-xs text-white/40">No matches. Try another locality.</div>}
                        {filtered.map((m) => (
                            <button
                                key={m}
                                data-testid={`mm-option-${m}`}
                                onClick={() => { onPick(m); setOpen(false); setQ(""); }}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-[#D4AF37]/10 ${selected === m ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "text-white/80"}`}
                            >
                                {m}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {selected && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-[#D4AF37] text-[#D4AF37] text-xs" data-testid="mm-selected">
                    <MapPin size={12} strokeWidth={1.5} /> {selected}
                </div>
            )}

            <div className="mt-4">
                <Label>All localities in {city.name}</Label>
                <div className="flex flex-wrap gap-2" data-testid="mm-popular">
                    {all.slice(0, 8).map((m) => (
                        <Chip key={m} active={selected === m} onClick={() => onPick(m)} testid={`mm-pop-${m}`}>{m}</Chip>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ResultCard({ result, loading, onDetailedEstimate, onDownloadPdf, waHref }) {
    if (!result) {
        return <div className="bg-[#0A0B10] border border-white/10 p-8 text-white/40 text-sm">Preparing your estimate...</div>;
    }
    return (
        <div
            data-testid="result-card"
            className={`relative bg-gradient-to-br from-[#0A0B10] to-[#12121A] border-2 border-[#D4AF37]/40 p-6 sm:p-8 shadow-[0_20px_80px_rgba(212,175,55,0.15)] ${loading ? "opacity-90" : ""}`}
        >
            <div className="text-xs uppercase tracking-widest text-white/50">AI-Estimated Indicative CPL</div>
            <div className="font-serif-display text-4xl sm:text-5xl text-[#D4AF37] leading-none mt-1" data-testid="result-cpl"
                style={{ textShadow: "0 0 30px rgba(212,175,55,0.3)" }}>
                {fmtINR(result.cpl_low)} – {fmtINR(result.cpl_high)}
            </div>
            <div className="mt-2 text-xs text-white/50">per {result.lead_tier} lead · pricing rounded to nearest ₹10</div>

            <div className="mt-5 pt-5 border-t border-white/10">
                <div className="text-xs uppercase tracking-widest text-white/50">Estimated cost for {result.lead_count} leads</div>
                <div className="mt-1 font-serif-display text-2xl text-white" data-testid="result-cost">
                    {fmtINR(result.estimated_cost_low)} – {fmtINR(result.estimated_cost_high)}
                </div>
            </div>

            {result.alt_volume_tiers?.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2" data-testid="alt-volume-tiers">
                    {result.alt_volume_tiers.map((t) => (
                        <div key={t.lead_count} className="p-2.5 border border-white/10 text-center">
                            <div className="text-[10px] text-white/40 uppercase tracking-widest">{t.lead_count} leads</div>
                            <div className="text-xs text-white/75 mt-1">{fmtINR(t.cost_low)}–{fmtINR(t.cost_high)}</div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-2" data-testid="cpl-factors">
                <Factor label="Market Demand" value={result.market_demand} />
                <Factor label="Competition" value={result.competition} />
                <Factor label="Price Positioning" value={result.price_positioning} />
                <Factor label="Confidence" value={result.confidence} highlight={CONFIDENCE_STYLE[result.confidence]} />
            </div>

            {result.confidence_note && (
                <div className="mt-3 text-[11px] text-white/45 italic leading-relaxed" data-testid="confidence-note">
                    {result.confidence_note}
                </div>
            )}

            <div className="mt-3 text-[11px] text-white/50 leading-relaxed">{result.explanation}</div>
            <div className="mt-2 text-[10px] text-white/35 leading-relaxed" data-testid="cpl-disclaimer">{result.disclaimer}</div>
            <div className="mt-1 text-[11px] text-[#D4AF37]/80 uppercase tracking-widest">Estimated results — not guaranteed.</div>

            <div className="mt-6 space-y-2">
                <button data-testid="detailed-estimate-btn" onClick={onDetailedEstimate} className="btn-gold w-full justify-center">
                    Get Detailed Estimate <ArrowRight size={16} />
                </button>
                <div className="grid grid-cols-2 gap-2">
                    <a data-testid="talk-to-expert-btn" href={waHref} target="_blank" rel="noopener noreferrer" className="btn-outline-gold justify-center text-xs">
                        <MessageCircle size={14} /> Talk to Lead Krafts
                    </a>
                    <button data-testid="download-pdf-btn" onClick={onDownloadPdf} className="btn-outline-gold justify-center text-xs">
                        <Download size={14} /> PDF Summary
                    </button>
                </div>
            </div>
        </div>
    );
}

function Factor({ label, value, highlight }) {
    return (
        <div className={`p-2.5 border ${highlight || "border-white/10"}`} data-testid={`factor-${label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className="text-[9px] uppercase tracking-widest text-white/45">{label}</div>
            <div className="mt-1 text-sm font-medium">{value}</div>
        </div>
    );
}
